import React, {useEffect, useMemo, useState} from 'react';
import tradingService from '../services/tradingService';
import {isCanceledRequest} from '../services/api';
import './AccountPerformanceChart.css';

const formatUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';

  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })} USDT`;
};

const formatDate = (value) => {
  if (!value) return '—';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleDateString();
};

function buildPath(points, width, height, padding) {
  if (!points.length) return '';

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return points
    .map((point, index) => {
      const x =
        points.length === 1
          ? width / 2
          : padding + (index / (points.length - 1)) * (width - padding * 2);

      const y = height - padding - ((point.value - min) / range) * (height - padding * 2);

      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function buildAreaPath(points, width, height, padding) {
  if (!points.length) return '';

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const baseline = height - padding;

  const linePoints = points.map((point, index) => {
    const x =
      points.length === 1
        ? width / 2
        : padding + (index / (points.length - 1)) * (width - padding * 2);

    const y = height - padding - ((point.value - min) / range) * (height - padding * 2);
    return { x: x.toFixed(2), y: y.toFixed(2) };
  });

  const first = linePoints[0];
  const last = linePoints[linePoints.length - 1];

  return [
    `M ${first.x} ${baseline.toFixed(2)}`,
    `L ${first.x} ${first.y}`,
    ...linePoints.slice(1).map((point) => `L ${point.x} ${point.y}`),
    `L ${last.x} ${baseline.toFixed(2)}`,
    'Z',
  ].join(' ');
}

export default function AccountPerformanceChart({ number, className = '' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
    const [initialBalance, setInitialBalance] = useState(null);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.accounts.performance.history({
          number,
          signal: ac.signal,
        });

        const rows = res?.data?.history ?? [];
        setHistory(Array.isArray(rows) ? rows : []);
          setInitialBalance(res?.data?.initial_balance_usdt ?? null);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setHistory([]);
        setError(err?.message || 'Failed to load performance history');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [number]);

  const chart = useMemo(() => {
    const points = history
      .map((row) => ({
        date: row.date,
        value: Number(row.net_usdt),
      }))
      .filter((row) => Number.isFinite(row.value));

    const width = 720;
    const height = 260;
    const padding = 32;
    const path = buildPath(points, width, height, padding);
    const areaPath = buildAreaPath(points, width, height, padding);

    const latest = points[points.length - 1] ?? null;
    const first = points[0] ?? null;
      const initial = Number.isFinite(Number(initialBalance))
          ? Number(initialBalance)
          : first?.value ?? null;
      const change = latest && initial !== null ? latest.value - initial : null;

    return {
      points,
      width,
      height,
      padding,
      path,
      areaPath,
      latest,
      first,
        initial,
      change,
    };
  }, [history, initialBalance]);

  return (
    <section className={`account-performance ${className}`}>
          <div className="account-performance__head">
            <div>
              <div className="account-performance__kicker">Performance</div>
              <h2 className="account-performance__title">Total asset value history</h2>
            </div>

            <div className="account-performance__summary">
              <span>Latest</span>
              <strong>{formatUsdt(chart.latest?.value)}</strong>
            </div>
          </div>

          {error ? <div className="account-performance__error">{error}</div> : null}
          {loading ? <div className="account-performance__empty">Loading performance…</div> : null}

          {!loading && !chart.points.length ? (
            <div className="account-performance__empty">No total USDT value history yet.</div>
          ) : null}

          {!loading && chart.points.length ? (
            <>
              <div className="account-performance__chart-wrap">
                <svg
                  className="account-performance__chart"
                  viewBox={`0 0 ${chart.width} ${chart.height}`}
                  role="img"
                  aria-label="Account total asset value history in USDT"
                >
              <line
                x1={chart.padding}
                y1={chart.height - chart.padding}
                x2={chart.width - chart.padding}
                y2={chart.height - chart.padding}
                className="account-performance__axis"
              />
              <line
                x1={chart.padding}
                y1={chart.padding}
                x2={chart.padding}
                y2={chart.height - chart.padding}
                className="account-performance__axis"
              />

              <path d={chart.areaPath} className="account-performance__area" />
              <path d={chart.path} className="account-performance__line" />

              {chart.points.map((point, index) => {
                const values = chart.points.map((p) => p.value);
                const min = Math.min(...values);
                const max = Math.max(...values);
                const range = max - min || 1;

                const x =
                  chart.points.length === 1
                    ? chart.width / 2
                    : chart.padding +
                      (index / (chart.points.length - 1)) * (chart.width - chart.padding * 2);

                const y =
                  chart.height -
                  chart.padding -
                  ((point.value - min) / range) * (chart.height - chart.padding * 2);

                return (
                  <circle
                    key={`${point.date}-${index}`}
                    cx={x}
                    cy={y}
                    r="3.5"
                    className="account-performance__point"
                  >
                    <title>
                      {formatDate(point.date)} · {formatUsdt(point.value)}
                    </title>
                  </circle>
                );
              })}
            </svg>
          </div>

          <div className="account-performance__footer">
            <span>
              From {formatUsdt(chart.initial)} to {formatDate(chart.latest?.date)}
            </span>
            <span className={Number(chart.change) >= 0 ? 'is-positive' : 'is-negative'}>
              Change: {formatUsdt(chart.change)}
            </span>
          </div>
        </>
      ) : null}
    </section>
  );
}
